// components/popup/ProhibitionCourtPopup.tsx
import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import {
  updateAssetReferral,
  updateAssetReferralProhibitionRequest,
} from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";

interface ProhibitionCourtPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  prohibitionId: string;
  existingData?: {
    prohabition_court_name?: string | null;
    prohabition_court_level?: string | null;
    prohabition_court_region?: string | null;
    prohabition_court_zone?: string | null;
    prohabition_court_woreda?: string | null;
    prohabition_request_first_hearing_date?: string | null;
    prohabition_request_court_registration_number?: string | null;
  };
  onSuccess?: () => void;
}

export const ProhibitionCourtPopup: React.FC<ProhibitionCourtPopupProps> = ({
  isOpen,
  onClose,
  referralId,
  existingData,
  prohibitionId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    prohabition_court_name: "",
    prohabition_court_level: "",
    prohabition_court_region: "",
    prohabition_court_zone: "",
    prohabition_court_woreda: "",
    prohabition_request_first_hearing_date: "",
    prohabition_request_court_registration_number: "",
  });

  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();

  // Pre-fill form with existing data
  useEffect(() => {
    if (existingData && isOpen) {
      setFormData({
        prohabition_court_name: existingData.prohabition_court_name || "",
        prohabition_court_level: existingData.prohabition_court_level || "",
        prohabition_court_region: existingData.prohabition_court_region || "",
        prohabition_court_zone: existingData.prohabition_court_zone || "",
        prohabition_court_woreda: existingData.prohabition_court_woreda || "",
        prohabition_request_first_hearing_date:
          existingData.prohabition_request_first_hearing_date || "",
        prohabition_request_court_registration_number:
          existingData.prohabition_request_court_registration_number || "",
      });
    }
  }, [existingData, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add this validation function inside the ProhibitionCourtPopup component
  const validateForm = () => {
    if (!formData.prohabition_request_court_registration_number)
      return "Court Registration Number is required.";
    if (!formData.prohabition_court_name) return "Court Name is required.";
    if (!formData.prohabition_court_level) return "Court Level is required.";
    if (!formData.prohabition_request_first_hearing_date)
      return "First Hearing Date is required.";
    //   if (!formData.prohabition_court_region)
    //     return "Court Region is required.";
    //   if (!formData.prohabition_court_zone)
    //     return "Court Zone is required.";
    //   if (!formData.prohabition_court_woreda)
    //     return "Court Woreda is required.";
    return "";
  };

  // Update the handleSubmit function to include validation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Create FormData for the update
      const submitFormData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (formData[key as keyof typeof formData]) {
          submitFormData.append(key, formData[key as keyof typeof formData]);
        }
      });

      // Call API to update referral
      await updateAssetReferralProhibitionRequest(
        prohibitionId,
        submitFormData,
      );

      // Show success message
      showTooltip("Prohibition court details updated successfully!", "success");

      // Reset form and close popup
      handleClose();

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error updating prohibition court details:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to update prohibition court details";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      prohabition_court_name: "",
      prohabition_court_level: "",
      prohabition_court_region: "",
      prohabition_court_zone: "",
      prohabition_court_woreda: "",
      prohabition_request_first_hearing_date: "",
      prohabition_request_court_registration_number: "",
    });
    onClose();
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Prohibition Court Details"
      description="Add prohibition court information"
    >
      <form onSubmit={handleSubmit}>
        {/* Court Registration Number */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">
            Court Registration Number
          </label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="prohabition_request_court_registration_number"
            placeholder="Enter court registration number"
            value={formData.prohabition_request_court_registration_number}
          />
        </div>

        {/* Court Name */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Court Name</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="prohabition_court_name"
            placeholder="Enter court name"
            value={formData.prohabition_court_name}
          />
        </div>

        {/* Court Level */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Court Level</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="prohabition_court_level"
            value={formData.prohabition_court_level}
            placeholder="Enter court level"
          />
        </div>

        {/* First Hearing Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">First Hearing Date</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="prohabition_request_first_hearing_date"
            value={formData.prohabition_request_first_hearing_date}
          />
        </div>

        {/* Court Location - Region, Zone, Woreda */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="flex flex-col">
            <label className="referral-popup-label">Region</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="prohabition_court_region"
              placeholder="Region"
              value={formData.prohabition_court_region}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Zone</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="prohabition_court_zone"
              placeholder="Zone"
              value={formData.prohabition_court_zone}
            />
          </div>

          <div className="flex flex-col">
            <label className="referral-popup-label">Woreda</label>
            <input
              type="text"
              className="referral-form-field"
              onChange={handleInputChange}
              name="prohabition_court_woreda"
              placeholder="Woreda"
              value={formData.prohabition_court_woreda}
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </BasePopup>
  );
};
